import React from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import type { AlertColor } from '@mui/material';

import type { LCalcConditionResult, UnitConditionGroup } from 'utils/lCalcConditionResult';

// 分類ごとの alert。unit 単位でサブ見出し + 条件一覧を表示する（L は複数 unit を扱うため）。
const UnitGroupedAlert: React.FC<{
    severity: AlertColor;
    title: string;
    caption?: string;
    groups: UnitConditionGroup[];
}> = ({ severity, title, caption, groups }) => {
    if (groups.length === 0) return null;
    return (
        <Alert severity={severity} sx={{ mt: 2 }}>
            <AlertTitle>{title}</AlertTitle>
            {caption && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>{caption}</Typography>
            )}
            {groups.map((group) => (
                <Box key={group.unitLabel} sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{group.unitLabel}</Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                        {group.conditions.map((label) => (
                            <Typography component="li" variant="body2" key={label}>{label}</Typography>
                        ))}
                    </Box>
                </Box>
            ))}
        </Alert>
    );
};

// L 側一括計算の 4 分類サマリ表示（達成度判断・周波数変換の 2 コンポーネント共通、issue #32）。
// R 側 CalcConditionResultAlerts と同じ配色・区分だが、各分類を unit 単位でまとめて見せる。
// succeeded / missing_data / failed を表示し、skipped_no_data は表示しない。
// 全カテゴリが空（全条件 skipped_no_data＝計算対象データ未登録）でも無反応にはせず、
// 条件一覧なしの案内だけを出す。
export const LCalcConditionResultAlerts: React.FC<{ result: LCalcConditionResult | null }> = ({ result }) => {
    if (!result) return null;
    const isEmpty =
        result.succeeded.length === 0 && result.missing_data.length === 0 && result.failed.length === 0;
    if (isEmpty) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>計算対象となる条件がありませんでした</AlertTitle>
                <Typography variant="body2">
                    データ登録ページから対象条件のデータを登録してください。
                </Typography>
            </Alert>
        );
    }
    return (
        <>
            <UnitGroupedAlert
                severity="success"
                title="計算が完了した条件"
                groups={result.succeeded}
            />
            <UnitGroupedAlert
                severity="warning"
                title="必要なデータが一部不足しているため計算できなかった条件"
                caption="一部のデータは登録済みですが、計算に必要なデータが揃っていません。データ登録ページから不足しているデータを登録してください。"
                groups={result.missing_data}
            />
            <UnitGroupedAlert
                severity="error"
                title="計算に失敗した条件"
                groups={result.failed}
            />
        </>
    );
};
